/**
 * Converts an image URL to a base64 data URI.
 * This is necessary for @react-pdf/renderer which has CORS issues with external image URLs.
 *
 * Uses fetch + FileReader instead of an <img> + canvas. The product renders the same
 * URLs as plain <img> tags (no crossOrigin), which populates Chrome's image cache with
 * CORS-less responses; a crossOrigin <img> against the same URL then reuses the poisoned
 * cache entry and fails the CORS check. fetch with cache: 'reload' bypasses that entry.
 *
 * @param url - The URL of the image to convert
 * @returns A promise that resolves to the base64 data URI, or null if conversion fails
 */
export async function imageUrlToBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, { mode: 'cors', cache: 'reload' });

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();

    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('FILEREADER_FAILED'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}
